"use client";

import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Mail, Send, Plus, Trash2, Pencil, QrCode, Bell } from "lucide-react";
import { usePermissions } from "@/hooks/use-permissions";
import { NOTIFICATIONS_PERMISSIONS } from "@/lib/auth/permissions.constants";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useNotificationChannels,
  type EmailChannelConfigInput,
  type NotificationChannelView,
} from "@/hooks/notifications/use-notification-channels";
import { isChannelConfigDeliverable } from "@/lib/notifications/deliverability";
import { describeChannelDestination } from "@/lib/notifications/recipients";
import { EmailChannelDialog } from "./email-channel-dialog";
import { TelegramConnectDialog } from "./telegram-connect-dialog";
import { TelegramEnrollmentDialog } from "./telegram-enrollment-dialog";
import { AccountAlertsCard } from "./account-alerts-card";
import { QualityDigestCard } from "./quality-digest-card";
import { useTranslations } from "next-intl"


function ChannelRecipients({ channel }: { channel: NotificationChannelView }) {
  const destination = describeChannelDestination(channel.type, channel.config)
  return (
    <p className="truncate text-xs text-muted-foreground" title={destination.full}>
      {destination.short}
    </p>
  )
}

/** A channel can look configured and deliver nothing — the system one is seeded from an owner who may not exist. */
function hasNoRecipients(channel: NotificationChannelView): boolean {
  return (
    channel.type === "email" &&
    !isChannelConfigDeliverable("email", channel.config as Record<string, unknown>)
  );
}

export function NotificationChannelsPage() {
  const tUi = useTranslations("NotificationChannels.NotificationChannelsNotificationChannelsPage")
  const tCommonUi = useTranslations("Common")

  const { can } = usePermissions();
  const channels = useNotificationChannels();

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [editing, setEditing] = useState<NotificationChannelView | null>(null);
  const [telegramConnectOpen, setTelegramConnectOpen] = useState(false);
  const [enrollmentChannelId, setEnrollmentChannelId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<NotificationChannelView | null>(null);

  // The enrollment dialog reads the live channel from the list (not a snapshot)
  // so polled updates surface freshly captured chats without reopening.
  const enrollmentChannel = enrollmentChannelId
    ? channels.channels.find((c) => c.id === enrollmentChannelId) ?? null
    : null;

  // While the enrollment dialog is open, poll so a chat the user just started
  // (or shared the link with) appears within a few seconds — Telegram captures
  // it server-side via the webhook, with no client signal otherwise.
  const refreshChannels = channels.refresh;
  useEffect(() => {
    if (!enrollmentChannelId) return;
    const interval = setInterval(refreshChannels, 3000);
    return () => clearInterval(interval);
  }, [enrollmentChannelId, refreshChannels]);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  if (!can(NOTIFICATIONS_PERMISSIONS.MANAGE)) {
    return (
      <div className="p-6">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <AlertTitle><span key={tUi("accessRestricted")}>{tUi("accessRestricted")}</span></AlertTitle>
            <AlertDescription>
              <span key={tUi("youDonTHavePermissionToManageNotification")}>{tUi("youDonTHavePermissionToManageNotification")}</span></AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const openCreateEmail = () => {
    setEditing(null);
    setEmailDialogOpen(true);
  };
  const openEditEmail = (channel: NotificationChannelView) => {
    setEditing(channel);
    setEmailDialogOpen(true);
  };

  const saveEmail = async (name: string, config: EmailChannelConfigInput) => {
    if (editing) {
      await channels.updateChannel(editing.id, { name, config: config as unknown as Record<string, unknown> });
      toast.success(tUi("channelUpdated"));
    } else {
      await channels.createEmailChannel(name, config);
      toast.success(tUi("channelCreated"));
    }
  };

  const toggleEnabled = async (channel: NotificationChannelView, enabled: boolean) => {
    setBusyId(channel.id);
    try {
      await channels.updateChannel(channel.id, { enabled });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update channel");
    } finally {
      setBusyId(null);
    }
  };

  const runTest = async (channel: NotificationChannelView) => {
    setTestingId(channel.id);
    try {
      const delivery = await channels.testChannel(channel.id);
      const msg = delivery.publicMessage || `Test ${delivery.status}.`;
      if (delivery.status === "delivered") toast.success(msg);
      else if (delivery.status === "skipped") toast.info(msg);
      else toast.error(msg);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send test");
    } finally {
      setTestingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    const target = deleting;
    setDeleting(null);
    try {
      await channels.deleteChannel(target.id);
      toast.success(tUi("channelDeleted"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete channel");
    }
  };

  // Different kinds of thing — different places on the page, not one flat list.
  const ownChannels = channels.channels.filter((c) => !c.isSystem)
  const systemChannel = channels.channels.find((c) => c.isSystem) ?? null

  // A render function, not a nested component — the latter gets a fresh identity
  // every render and would remount the Switch.
  const renderChannel = (channel: NotificationChannelView) => (
    <Card key={channel.id} className={channel.isSystem ? "border-dashed bg-muted/40" : undefined}>
      <CardContent className="flex flex-wrap items-center gap-3 py-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {channel.type === "email" ? (
            <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <Send className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate font-medium">{channel.name}</span>
              <Badge variant="secondary">{channel.type}</Badge>
              {!channel.enabled && <Badge variant="outline"><span key={tUi("disabled")}>{tUi("disabled")}</span></Badge>}
            </div>
            <ChannelRecipients channel={channel} />
            {hasNoRecipients(channel) && (
              <p className="truncate text-xs text-destructive">
                <span key={tUi("noRecipientsAlertsAreNotBeingDelivered")}>{tUi("noRecipientsAlertsAreNotBeingDelivered")}</span></p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1">
          {/* The system channel is the fallback for alerts about stopped work — switching
              it off would silence them with nothing left to catch it. */}
          {!channel.isSystem && (
            <Switch
              checked={channel.enabled}
              disabled={busyId === channel.id}
              onCheckedChange={(v) => toggleEnabled(channel, v)}
              aria-label={channel.enabled ? tUi("disableChannel") : tUi("enableChannel")}
            />
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => runTest(channel)}
            disabled={testingId === channel.id}
          >
            <span key={testingId === channel.id ? tUi("testing") : tUi("test")}>{testingId === channel.id ? tUi("testing") : tUi("test")}</span>
          </Button>
          {channel.type === "email" ? (
            <Button size="sm" variant="ghost" onClick={() => openEditEmail(channel)} aria-label={tUi("edit")}>
              <Pencil className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEnrollmentChannelId(channel.id)}
              aria-label={tUi("manageChats")}
            >
              <QrCode className="h-4 w-4" />
            </Button>
          )}
          {/* Not deletable (the API 409s; it would be re-ensured anyway) — disable it
              instead. The slot stays reserved so the action column stays aligned. */}
          {channel.isSystem ? (
            <div className="h-9 w-9" aria-hidden />
          ) : (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setDeleting(channel)}
              aria-label={tUi("delete")}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )

  return (
    <div className="p-6">
      <div className="mx-auto max-w-4xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="flex items-center gap-2 text-2xl font-semibold">
              <Bell className="h-5 w-5" /> <span><span key={tUi("notificationChannels")}>{tUi("notificationChannels")}</span></span>
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              <span key={tUi("whereAlertsAreDeliveredWorkspaceAlertsAboutBilling")}>{tUi("whereAlertsAreDeliveredWorkspaceAlertsAboutBilling")}</span></p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setTelegramConnectOpen(true)}>
              <Send className="mr-1.5 h-4 w-4" /> <span><span key={tUi("connectTelegram")}>{tUi("connectTelegram")}</span></span>
            </Button>
            <Button onClick={openCreateEmail}>
              <Plus className="mr-1.5 h-4 w-4" /> <span><span key={tUi("newEmailChannel")}>{tUi("newEmailChannel")}</span></span>
            </Button>
          </div>
        </div>

        {channels.error && (
          <Alert variant="destructive">
            <AlertDescription>{channels.error}</AlertDescription>
          </Alert>
        )}

        {channels.isLoading ? (
          <div className="space-y-2">
            {[0, 1].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {ownChannels.length === 0 ? (
              <Card>
                <CardContent className="py-10 text-center text-sm text-muted-foreground">
                  <span key={tUi("noChannelsOfYourOwnYetCreateAn")}>{tUi("noChannelsOfYourOwnYetCreateAn")}</span></CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {ownChannels.map(renderChannel)}
              </div>
            )}

            {systemChannel && (
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <span key={tUi("providedByVersafy")}>{tUi("providedByVersafy")}</span></span>
                  <div className="h-px flex-1 bg-border" />
                </div>
                {renderChannel(systemChannel)}
                <p className="text-xs text-muted-foreground">
                  <span key={tUi("sendsFromTheVersafyAddressNoInboxOf")}>{tUi("sendsFromTheVersafyAddressNoInboxOf")}</span></p>
              </div>
            )}

            <AccountAlertsCard channels={channels.channels} />
            <QualityDigestCard channels={channels.channels} />
          </div>
        )}
      </div>

      <EmailChannelDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        channel={editing}
        onSave={saveEmail}
      />
      <TelegramConnectDialog
        open={telegramConnectOpen}
        onOpenChange={setTelegramConnectOpen}
        onConnect={channels.connectTelegram}
        onConnected={(channel) => setEnrollmentChannelId(channel.id)}
      />
      <TelegramEnrollmentDialog
        open={Boolean(enrollmentChannel)}
        onOpenChange={(open) => !open && setEnrollmentChannelId(null)}
        channel={enrollmentChannel}
        onRemoveChat={async (chatId) => {
          if (!enrollmentChannel) return;
          await channels.removeChat(enrollmentChannel.id, chatId);
        }}
      />

      <AlertDialog open={Boolean(deleting)} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle><span><span key={tUi("delete2")}>{tUi("delete2")}</span></span>{deleting?.name}<span>”?</span></AlertDialogTitle>
            <AlertDialogDescription>
              <span key={tUi("agentsThatSelectedThisChannelWillStopDelivering")}>{tUi("agentsThatSelectedThisChannelWillStopDelivering")}</span></AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}><span key={tUi("delete")}>{tUi("delete")}</span></AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
